import { Box, Container, Typography, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import CodeIcon from '@mui/icons-material/Code';
import SwapVerticalCircleOutlinedIcon from '@mui/icons-material/SwapVerticalCircleOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import { useLanguage } from '../contexts/LanguageContext';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
    : 'linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f8fafc 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '800px',
    height: '800px',
    background: theme.palette.mode === 'dark'
      ? 'radial-gradient(circle, rgba(37, 99, 235, 0.1) 0%, transparent 70%)'
      : 'radial-gradient(circle, rgba(37, 99, 235, 0.05) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
}));

const ServiceCard = styled(Box)(({ theme }) => ({
  padding: '48px',
  borderRadius: '28px',
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 41, 59, 0.5)'
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}12 0%, ${theme.palette.secondary.main}12 100%)`,
    opacity: 0,
    transition: 'opacity 0.5s ease',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '200%',
    height: '200%',
    background: `radial-gradient(circle, ${theme.palette.secondary.main}15 0%, transparent 70%)`,
    opacity: 0,
    transition: 'all 0.6s ease',
    transform: 'scale(0)',
  },
  '&:hover': {
    transform: 'translateY(-16px) scale(1.02)',
    border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(52, 211, 153, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 24px 80px rgba(16, 185, 129, 0.25), 0 0 60px rgba(37, 99, 235, 0.15)'
      : '0 24px 80px rgba(16, 185, 129, 0.2), 0 0 60px rgba(37, 99, 235, 0.1)',
    '&::before': {
      opacity: 1,
    },
    '&::after': {
      opacity: 1,
      transform: 'scale(1)',
    },
    '& .icon-wrapper': {
      transform: 'scale(1.1) rotate(5deg)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 12px 40px rgba(16, 185, 129, 0.4)'
        : '0 12px 40px rgba(16, 185, 129, 0.3)',
    },
    '& .icon-bg': {
      transform: 'scale(1.3)',
      opacity: 0.6,
    },
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '100px',
  height: '100px',
  borderRadius: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  marginBottom: '32px',
  position: 'relative',
  zIndex: 2,
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
    : '0 8px 32px rgba(16, 185, 129, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '-2px',
    borderRadius: '26px',
    padding: '2px',
    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0.5,
  },
  '& svg': {
    fontSize: '3rem',
    color: '#FFFFFF',
    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
    position: 'relative',
    zIndex: 1,
  },
}));

const IconBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '140px',
  height: '140px',
  borderRadius: '50%',
  background: `radial-gradient(circle, ${theme.palette.secondary.main}20 0%, transparent 70%)`,
  top: '-20px',
  left: '-20px',
  transition: 'all 0.5s ease',
  opacity: 0.4,
  zIndex: 0,
}));

export default function ServicesSection() {
  const { t } = useLanguage();

  const services = [
    {
      icon: <CodeIcon />,
      title: t('services.custom.title'),
      description: t('services.custom.desc'),
      path: '/services/software-development',
    },
    {
      icon: <SwapVerticalCircleOutlinedIcon />,
      title: t('services.digital.title'),
      description: t('services.digital.desc'),
      path: '/services/digital-transformation',
    },
    {
      icon: <EmojiObjectsOutlinedIcon />,
      title: t('services.consulting.title'),
      description: t('services.consulting.desc'),
      path: '/services/it-consulting',
    },
    {
      icon: <CloudOutlinedIcon />,
      title: t('services.cloud.title'),
      description: t('services.cloud.desc'),
      path: '/services/cloud-solutions',
    },
  ];

  return (
    <SectionContainer component="section" id="services" aria-labelledby="services-heading">
      <Container maxWidth="lg">
        <Stack spacing={3} alignItems="center" textAlign="center" sx={{ mb: 10 }}>
          <Typography
            id="services-heading"
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3rem' },
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-16px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '4px',
                borderRadius: '2px',
                background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              },
            }}
          >
            {t('services.title')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, lineHeight: 1.8, fontSize: '1.1rem', mt: 3 }}
          >
            {t('services.subtitle')}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
            },
            gap: 4,
          }}
        >
          {services.map((service, index) => (
            <ServiceCard 
              key={index}
              component={Link}
              to={service.path}
              sx={{ textDecoration: 'none' }}
            >
              <Box sx={{ position: 'relative' }}>
                <IconBackground className="icon-bg" />
                <IconWrapper className="icon-wrapper">{service.icon}</IconWrapper>
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  position: 'relative',
                  zIndex: 1,
                  fontSize: '1.5rem',
                }}
              >
                {service.title}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  lineHeight: 1.9,
                  position: 'relative',
                  zIndex: 1,
                  fontSize: '1.05rem',
                  flex: 1,
                }}
              >
                {service.description}
              </Typography>
            </ServiceCard>
          ))}
        </Box>
      </Container>
    </SectionContainer>
  );
}